// src/pages/admin/analytics/components/MaterialConsumptionForecastTab.jsx
import { TrendingUp, Calendar, Zap, RefreshCw, Package } from 'lucide-react';

const MaterialConsumptionForecastTab = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-500 mr-3" />
        <span className="font-medium text-lg">Predicting Demand & Raw Material Requirements...</span>
      </div>
    );
  }

  const forecastList = data?.forecastList || [];
  const totalForecast7DaysRevenue = data?.totalForecast7DaysRevenue || 0;

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-indigo-800 via-purple-800 to-slate-900 text-white rounded-xl p-6 shadow-lg">
        <div className="flex items-center space-x-3">
          <Zap className="h-7 w-7 text-yellow-400" />
          <div>
            <h2 className="text-xl font-black">AI Demand & Material Consumption Predictor</h2>
            <p className="text-xs text-indigo-200 mt-1">
              Calculates daily sales velocity over recent cycles to forecast upcoming 7-day and 30-day raw material & finished product replenishment needs.
            </p>
          </div>
        </div>
      </div>

      {/* Summary KPI Card */}
      <div className="bg-white rounded-xl p-5 border border-indigo-200 shadow-sm flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Forecasted 7-Day Revenue</p>
          <h3 className="text-3xl font-black text-gray-900 mt-1">
            Rs. {totalForecast7DaysRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </h3>
          <p className="text-xs text-gray-500 mt-1">Based on current daily sales velocity</p>
        </div>
        <div className="p-4 bg-indigo-50 rounded-xl">
          <TrendingUp className="h-8 w-8 text-indigo-600" />
        </div>
      </div>

      {/* Forecast Table */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 text-sm flex items-center">
            <Package className="h-4 w-4 text-indigo-600 mr-2" />
            Product Demand & Raw Material Consumption Forecast
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-100 border-b border-gray-200 text-gray-600 font-semibold uppercase tracking-wider">
              <tr>
                <th className="p-4">Product Name</th>
                <th className="p-4 text-center">Daily Velocity (Units/Day)</th>
                <th className="p-4 text-center">Forecasted 7-Day Demand</th>
                <th className="p-4 text-center">Forecasted 30-Day Demand</th>
                <th className="p-4 text-right">Forecasted 7-Day Sales (LKR)</th>
                <th className="p-4 text-left">Stock Replenishment Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-medium">
              {forecastList.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-gray-500">
                    Insufficient sales data to generate forecast predictions.
                  </td>
                </tr>
              ) : (
                forecastList.map((item) => (
                  <tr key={item.productId} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4 font-bold text-gray-900">{item.productName}</td>
                    <td className="p-4 text-center font-bold text-indigo-600">{item.dailyVelocity} / day</td>
                    <td className="p-4 text-center font-bold text-gray-800">{item.forecast7DaysQty} units</td>
                    <td className="p-4 text-center font-bold text-gray-800">{item.forecast30DaysQty} units</td>
                    <td className="p-4 text-right font-black text-emerald-700">
                      Rs. {item.forecast7DaysRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="p-4">
                      <span className="px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md font-semibold text-[11px]">
                        Order {item.forecast7DaysQty} units stock
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MaterialConsumptionForecastTab;
